import { Fragment } from "react";
import { Plus } from "lucide-react";
import { dec, eur } from "./format";

const Th = ({ children, right }) => (
  <th className={`py-2 px-2 text-[10px] uppercase tracking-widest font-semibold text-slate-500 ${right ? "text-right" : "text-left"}`}>{children}</th>
);

export const RisorseGruppo = ({ gruppo, eff, onRegistra }) => {
  const registrabile = onRegistra && gruppo.tipo !== "ALTRO";
  const somma = gruppo.risorse.reduce((s, r) => s + Number(r.importo_um || 0), 0);
  const dich = gruppo.totale_dichiarato;
  return (
    <div className="border rounded-md" data-testid={`analisi-gruppo-${gruppo.tipo}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-900 border-b">
        <span className="text-sm font-semibold">{gruppo.label} <span className="text-slate-400 font-normal">({gruppo.risorse.length})</span></span>
        <span className="text-xs text-slate-500">Totale per U.M.: <b className="font-mono text-slate-800 dark:text-slate-200" data-testid={`analisi-gruppo-tot-${gruppo.tipo}`}>{eur(dich ? dich.importo_um : somma, 4)}</b>
          {dich && <> · voce: <b className="font-mono">{eur(dich.importo_totale)}</b></>}</span>
        {registrabile && <button type="button" className="text-xs text-sky-700 hover:underline inline-flex items-center" onClick={() => onRegistra(gruppo.tipo)} data-testid={`analisi-registra-fuori-${gruppo.tipo}`}><Plus className="h-3 w-3 mr-0.5" /> Effettivo fuori analisi</button>}
      </div>
      {gruppo.risorse.length === 0 ? <p className="text-xs text-slate-500 px-3 py-2">Nessuna risorsa di questo tipo nell'analisi.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr><Th>Codice</Th><Th>Descrizione</Th><Th>U.M.</Th><Th right>Coefficiente</Th><Th right>Quantità</Th><Th right>Prezzo unit.</Th><Th right>Importo / U.M.</Th><Th right>Importo voce</Th>{registrabile && <Th />}</tr></thead>
            <tbody>{gruppo.risorse.map((r) => {
              const e = eff?.[r.id];
              return (
              <Fragment key={r.id}>
              <tr className="border-t border-slate-100 dark:border-slate-800" data-testid={`analisi-risorsa-${r.ordine}`}>
                <td className="py-1.5 px-2 font-mono text-xs whitespace-nowrap">{r.codice || "—"}</td>
                <td className="py-1.5 px-2 text-xs">{r.descrizione}</td>
                <td className="py-1.5 px-2 text-xs">{r.um || "—"}</td>
                <td className="py-1.5 px-2 text-right font-mono text-xs">{dec(r.coefficiente, 0, 6)}</td>
                <td className="py-1.5 px-2 text-right font-mono text-xs">{dec(r.quantita, 0, 4)}</td>
                <td className="py-1.5 px-2 text-right font-mono text-xs">{eur(r.prezzo_unitario, 4)}</td>
                <td className="py-1.5 px-2 text-right font-mono text-xs">{eur(r.importo_um, 4)}</td>
                <td className="py-1.5 px-2 text-right font-mono text-xs">{eur(r.importo)}</td>
                {registrabile && <td className="px-1"><button type="button" title="Registra effettivo su questa risorsa" onClick={() => onRegistra(gruppo.tipo, r)} className="text-sky-700" data-testid={`analisi-registra-${r.id}`}><Plus className="h-3.5 w-3.5" /></button></td>}
              </tr>
              {e && (
                <tr className="bg-sky-50/70 dark:bg-sky-950/20" data-testid={`analisi-effettivo-${r.id}`}>
                  <td className="py-1 px-2 text-[10px] uppercase tracking-widest font-semibold text-sky-700" colSpan={2}>↳ Effettivo registrato</td>
                  <td />
                  <td className="py-1 px-2 text-right font-mono text-[11px] text-sky-800" colSpan={2}>{Number(e.quantita) ? dec(e.quantita, 0, 4) : ""}{Number(e.ore) ? ` · ${dec(e.ore, 0, 2)} h` : ""}</td>
                  <td colSpan={2} />
                  <td className="py-1 px-2 text-right font-mono text-[11px] font-semibold text-sky-800">{eur(e.importo)}</td>
                  {registrabile && <td />}
                </tr>
              )}
              </Fragment>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const COMP = { MANODOPERA: "Manodopera", MATERIALE: "Materiali", NOLO: "Noli e attrezzature" };

export const StimeDichiarate = ({ stime }) => (
  <div className="border border-amber-200 rounded-md" data-testid="analisi-stime">
    <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-900">
      Stima dichiarata nel file, senza analisi: <b>nessuna risorsa elementare</b>. Sono mostrate solo le quote stimate per componente, come scritte nell'Excel.
    </div>
    <table className="w-full text-sm">
      <thead><tr><Th>Componente</Th><Th>Descrizione nel file</Th><Th right>Importo / U.M.</Th><Th right>Importo voce</Th></tr></thead>
      <tbody>{stime.map((s) => (
        <tr key={s.id} className="border-t border-slate-100" data-testid={`analisi-stima-${s.componente}`}>
          <td className="py-1.5 px-2 text-xs font-semibold">{COMP[s.componente] || s.componente}</td>
          <td className="py-1.5 px-2 text-xs">{s.descrizione || "—"}</td>
          <td className="py-1.5 px-2 text-right font-mono text-xs">{eur(s.importo_um, 4)}</td>
          <td className="py-1.5 px-2 text-right font-mono text-xs">{eur(s.importo)}</td>
        </tr>
      ))}</tbody>
    </table>
  </div>
);
